import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { FormGroup } from 'reactstrap';

import {
	buildImageSrcSet,
	buildImageSizesForContentGrid,
	resizeImage,
	IMAGE_WIDTHS,
} from '@techchange/filestack';
import { FormField, tcFormValidators, FIELD_TYPES } from '@techchange/components';
import { appPropTypes, useLanguageContext } from '@techchange/components';
import { messages } from '../../../../i18n';

import { RESOURCE_TYPES } from '../../../../constants';
import ContentEditor from '../../ContentEditor';
import {
	getInitialLanguage,
	useResourceFormData,
	RESOURCE_FORM_FIELDS,
} from '../../hooks/useResourceFormData';
import { getLinkTargetOptions } from '../../utils/fieldOptions';
import { contentBuilderPropTypes, imageDataShape, initialValuesShape } from '../../utils/propTypes';

const propTypes = {

	...contentBuilderPropTypes.contentEditor,

	/**
	 * The Resource type.
	 */
	type: PropTypes.oneOf([RESOURCE_TYPES.IMAGE]).isRequired,

	/**
	 * The initial values of the form.
	 */
	initialValues: PropTypes.shape({

		...initialValuesShape,

		/**
		 * The content data.
		 */
		data: PropTypes.shape(imageDataShape),

	}).isRequired,

	/**
	 * The settings for filestack to handle image uploads and rendering.
	 */
	filestack: appPropTypes.filestack.isRequired,

};

const DEFAULT_RESOURCE_CONTENT = {
	[RESOURCE_FORM_FIELDS.TITLE]: '',
	[RESOURCE_FORM_FIELDS.DATA]: { target: '_blank' },
};

const getInitialFormData = (props) => {

	const { title, data, width, resourceContents, deletedResourceContents } = props.initialValues;

	return {
		[RESOURCE_FORM_FIELDS.TITLE]: title || '',
		[RESOURCE_FORM_FIELDS.DATA]: {
			src: data?.src,
			href: data?.href,
			alt: data?.alt,
			target: data?.target || '_blank',
		},
		[RESOURCE_FORM_FIELDS.LANGUAGE]: getInitialLanguage(props),
		[RESOURCE_FORM_FIELDS.WIDTH]: width || 12,
		resource_contents: resourceContents || [{
			[RESOURCE_FORM_FIELDS.LANGUAGE]: props.defaultLanguage,
			...DEFAULT_RESOURCE_CONTENT,
		}],
		deleted_resource_contents: deletedResourceContents || [],
	};

};

const getInitialInputValidation = () => ({
	[RESOURCE_FORM_FIELDS.TITLE]: [tcFormValidators.required],
	[RESOURCE_FORM_FIELDS.DATA]: {
		src: [tcFormValidators.required],
		href: [tcFormValidators.url],
	},
	resource_contents: [tcFormValidators.arrayContainingObjects(RESOURCE_FORM_FIELDS.LANGUAGE, {
		[RESOURCE_FORM_FIELDS.TITLE]: [tcFormValidators.required],
		[RESOURCE_FORM_FIELDS.DATA]: {
			src: [tcFormValidators.required],
			href: [tcFormValidators.url],
		},
	})],
});

/**
 * Editor for "image" resource types. Admins can makes changes to the "title", "data.src",
 * "data.alt", "data.href", and "data.target" fields for image resources.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object}  The component to render.
 */
const ImageContentEditor = (props) => {

	const {
		id,
		type,
		filestack,
		onCancel,
	} = props;

	const intl = useIntl();

	const { preferredLanguage, defaultLanguage } = useLanguageContext();

	const formProps = {
		...props,
		userLanguage: preferredLanguage,
		defaultLanguage,
	};

	const { formData, inputValidation, actions } = useResourceFormData(
		() => getInitialFormData(formProps),
		getInitialInputValidation,
		{ ...formProps, defaultResourceContent: DEFAULT_RESOURCE_CONTENT }
	);

	const LINK_TARGET_OPTIONS = getLinkTargetOptions(intl);

	const activeTranslation = formData.resource_contents
		.find((rContent) => rContent.language === formData.language);

	const srcSet = buildImageSrcSet(
		activeTranslation?.[RESOURCE_FORM_FIELDS.DATA]?.src,
		IMAGE_WIDTHS
	);

	const activeInputValidation = inputValidation?.resource_contents?.data?.[formData.language];

	const formFields = activeTranslation && (
		<>
			<FormGroup>
				<FormField
					type={FIELD_TYPES.TEXT}
					name={RESOURCE_FORM_FIELDS.TITLE}
					value={activeTranslation[RESOURCE_FORM_FIELDS.TITLE]}
					label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_IMAGE_FIELD_LABEL_TEXT_TITLE)}
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_IMAGE_FIELD_PLACEHOLDER_TEXT_TITLE)}
					isInvalid={activeInputValidation?.[RESOURCE_FORM_FIELDS.TITLE]?.invalid}
					error={activeInputValidation?.[RESOURCE_FORM_FIELDS.TITLE]?.error}
					isEditing
					isRequired
					onChange={actions.updateField} />
			</FormGroup>
			<FormGroup>
				<FormField
					id={`src-file-upload-field-${id}`}
					type={FIELD_TYPES.FILE_UPLOAD}
					name="src"
					value={activeTranslation?.[RESOURCE_FORM_FIELDS.DATA]?.src}
					label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_IMAGE_FIELD_LABEL_TEXT_IMAGE)}
					accept={['image/*']}
					visibility="public"
					filestack={filestack}
					isInvalid={activeInputValidation?.[RESOURCE_FORM_FIELDS.DATA]?.src?.invalid}
					error={activeInputValidation?.[RESOURCE_FORM_FIELDS.DATA]?.src?.error}
					isEditing
					isRequired
					onChange={actions.updateField} />
				{activeTranslation?.[RESOURCE_FORM_FIELDS.DATA]?.src &&
					<div className="mb-2">
						<img
							alt={intl.formatMessage(messages.TC_COMPONENTS_GENERAL_PREVIEW_LABEL)}
							className="img-fluid w-100"
							srcSet={srcSet}
							sizes={srcSet && buildImageSizesForContentGrid(formData.width)}
							src={resizeImage(activeTranslation[RESOURCE_FORM_FIELDS.DATA].src)} />
					</div>
				}
			</FormGroup>
		</>
	);

	const advancedFormFields = activeTranslation && (
		<>
			<FormGroup>
				<FormField
					type={FIELD_TYPES.TEXT}
					name="alt"
					value={activeTranslation[RESOURCE_FORM_FIELDS.DATA]?.alt || ''}
					label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_IMAGE_FIELD_LABEL_TEXT_ALT)}
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_IMAGE_FIELD_PLACEHOLDER_TEXT_ALT)}
					help={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_IMAGE_FIELD_HELP_TEXT_ALT)}
					isEditing
					onChange={actions.updateField} />
			</FormGroup>
			<FormGroup>
				<FormField
					type={FIELD_TYPES.TEXT}
					name="href"
					value={activeTranslation[RESOURCE_FORM_FIELDS.DATA]?.href || ''}
					label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_IMAGE_FIELD_LABEL_TEXT_HREF)}
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_IMAGE_FIELD_PLACEHOLDER_TEXT_HREF)}
					isInvalid={activeInputValidation?.[RESOURCE_FORM_FIELDS.DATA]?.href?.invalid}
					error={activeInputValidation?.[RESOURCE_FORM_FIELDS.DATA]?.href?.error}
					isEditing
					onChange={actions.updateField} />
			</FormGroup>
			<FormGroup>
				<FormField
					id={`target-select-field-${id}`}
					type={FIELD_TYPES.SELECT}
					name="target"
					value={activeTranslation?.[RESOURCE_FORM_FIELDS.DATA]?.target}
					options={LINK_TARGET_OPTIONS.map((option) => ({
						id: option.value,
						label: option.label,
						value: option.value,
					}))}
					label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_IMAGE_FIELD_LABEL_TEXT_LINK_TARGET)}
					isEditing
					onChange={actions.updateField} />
			</FormGroup>
		</>
	);

	return (
		<ContentEditor
			id={id}
			type={type}
			formData={formData}
			inputValidation={inputValidation}
			actions={actions}
			formFields={formFields}
			advancedFormFields={advancedFormFields}
			onCancel={onCancel} />
	);

};

ImageContentEditor.propTypes = propTypes;

export default ImageContentEditor;
